import {
  Box,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  IconButton,
  Tooltip,
  Button,
  TextareaAutosize,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Collapse,
} from "@mui/material";
import { useEffect, useState } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FieldsConfiguration from "./FieldsConfiguration";
import { GetDeviceFieldsHistory, UpdateDeviceFields } from "../Api";

/**
 * Interface for a single device field.
 */
interface DeviceField {
  name: string;
  inputType: string;
  outputType: string;
  clickhouseColumn: string;
  factor: number;
  isArray: boolean;
  arraySize: number;
}

/**
 * Interface for a sensor field group, associated with a unique sensor ID.
 */
interface SensorFieldGroup {
  sensor_id: string;
  fields: DeviceField[];
}

/**
 * Props expected by the SettingPage component.
 */
interface Props {
  selectedDevice: {
    deviceID: string;
    name?: string;
  };
}

// Default field template for new entries
const defaultField: DeviceField = {
  name: "",
  inputType: "uint8",
  outputType: "float32",
  clickhouseColumn: "",
  factor: 1,
  isArray: false,
  arraySize: 1,
};

/**
 * Main component that manages sensor field configuration.
 * Supports viewing, editing, adding, and deleting sensor ID groups.
 *
 * @param {Props} props - Component props containing selected device information.
 * @returns {JSX.Element}
 */
const SettingPage: React.FC<Props> = ({ selectedDevice }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [fetchedData, setFetchedData] = useState<SensorFieldGroup[]>([]);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editableJson, setEditableJson] = useState("");
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [isNewSensor, setIsNewSensor] = useState(false);
  const [collapsedIndexes, setCollapsedIndexes] = useState<Set<number>>(
    new Set()
  );
  const [sensorToDeleteIndex, setSensorToDeleteIndex] = useState<number | null>(
    null
  );

  /**
   * Fetches device field history for the selected device on mount or device change.
   */
  useEffect(() => {
    GetDeviceFieldsHistory(selectedDevice.deviceID).then((res) => {
      if (Array.isArray(res?.data)) {
        const updated = res.data.map((item: any, index: number) => ({
          sensor_id: String(index),
          fields: item.fields,
        }));
        setFetchedData(updated);
      }
    });
  }, [selectedDevice]);

  /**
   * Handles tab change for view vs. add mode.
   */
  const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  /**
   * Copies the specified JSON data to the clipboard.
   *
   * @param {any} data - Data to copy.
   */
  const handleCopyToClipboard = (data: any) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
  };

  /**
   * Enables edit mode for a specific sensor group.
   *
   * @param {number} index - Index of sensor to edit.
   */
  const handleEdit = (index: number) => {
    setEditIndex(index);
    setEditableJson(JSON.stringify(fetchedData[index], null, 2));
    handleToggleCollapse(index);
  };

  /**
   * Adds a new field to the currently editable sensor group.
   */
  const handleAddNewField = () => {
    if (editIndex === null) return;
    try {
      const current = JSON.parse(editableJson) as SensorFieldGroup;
      current.fields.push({ ...defaultField });
      setEditableJson(JSON.stringify(current, null, 2));
    } catch (e) {
      alert("Failed to parse current JSON. Please check formatting.");
    }
  };

  /**
   * Validates and saves edited JSON back to state and API.
   */
  const handleSave = async () => {
    try {
      const newSensorData: SensorFieldGroup = JSON.parse(editableJson);
      for (let i = 0; i < newSensorData.fields.length; i++) {
        const field = newSensorData.fields[i];
        if (
          !field.name.trim() ||
          !field.inputType.trim() ||
          !field.outputType.trim() ||
          !field.clickhouseColumn.trim()
        ) {
          alert(`Field #${i + 1} has missing required values.`);
          return;
        }
      }
      const updatedList = [...fetchedData];
      updatedList[editIndex!] = newSensorData;

      const finalPayload = {
        device_id: selectedDevice.deviceID,
        data: updatedList.map((item) => ({
          slave_id: item.sensor_id,
          fields: item.fields,
        })),
      };

      await UpdateDeviceFields(selectedDevice.deviceID, finalPayload);
      setFetchedData(updatedList);
      setEditIndex(null);
    } catch (error) {
      console.error("Invalid JSON or update failed:", error);
      alert("Invalid JSON or update failed.");
    }
  };

  /**
   * Adds a new sensor group to the top of the list.
   */
  const handleAddNewSensor = () => {
    const defaultNewSensor: SensorFieldGroup = {
      sensor_id: "0",
      fields: [{ ...defaultField }],
    };

    const shiftedSensors = fetchedData.map((sensor, index) => ({
      ...sensor,
      sensor_id: `${index + 1}`,
    }));

    setFetchedData([defaultNewSensor, ...shiftedSensors]);
    setEditIndex(0);
    setEditableJson(JSON.stringify(defaultNewSensor, null, 2));
    setIsNewSensor(true);

    handleToggleCollapse(0);
  };

  /**
   * Triggers a dialog for confirming deletion of a sensor.
   *
   * @param {number} index - Index to delete.
   */
  const handleDeleteSensorClick = (index: number) => {
    setSensorToDeleteIndex(index);
    setOpenConfirmDialog(true);
  };

  /**
   * Confirms and processes deletion of a sensor group.
   */
  const handleConfirmDelete = async () => {
    if (sensorToDeleteIndex === null) return;

    const updatedList = [...fetchedData];
    updatedList.splice(sensorToDeleteIndex, 1);

    const reindexedList = updatedList.map((item, idx) => ({
      ...item,
      sensor_id: String(idx),
    }));

    const finalPayload = {
      device_id: selectedDevice.deviceID,
      data: reindexedList.map((item) => ({
        slave_id: item.sensor_id,
        fields: item.fields,
      })),
    };

    try {
      await UpdateDeviceFields(selectedDevice.deviceID, finalPayload);
      setFetchedData(reindexedList);
      setEditIndex(null);
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Failed to delete Device Number (Sensor ID).");
    }

    setSensorToDeleteIndex(null);
    setOpenConfirmDialog(false);
  };

  /**
   * Cancels sensor deletion dialog.
   */
  const handleCancelDelete = () => {
    setOpenConfirmDialog(false);
    setSensorToDeleteIndex(null);
  };

  /**
   * Cancels edit mode and reverts to previous state.
   * If a new sensor was added, it removes it from the list.
   */
  const handleCancelEdit = () => {
    if (isNewSensor) {
      const updated = [...fetchedData];
      updated.splice(0, 1);

      const reIndexed = updated.map((sensor, index) => ({
        ...sensor,
        sensor_id: `${index}`,
      }));

      setFetchedData(reIndexed);
      setIsNewSensor(false);
    }
    setEditIndex(null);
    setEditableJson("");

    setCollapsedIndexes(new Set());
  };

  /**
   * Toggles the collapsed state for a given sensor index.
   *
   * @param {number} index - Index to toggle.
   */
  const handleToggleCollapse = (index: number) => {
    setCollapsedIndexes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  return (
    <Box>
      <Tabs value={tabIndex} onChange={handleChangeTab} centered>
        <Tab label="View Added Fields" />
        <Tab label="Add New Fields" />
      </Tabs>

      {tabIndex === 0 && (
        <Stack spacing={2} mt={2}>
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddNewSensor}
            >
              Add Device Number (Sensor ID)
            </Button>
          </Box>

          {fetchedData.length === 0 ? (
            <Typography>No fields added yet.</Typography>
          ) : (
            fetchedData.map((sensor, idx) => {
              return (
                <Paper key={idx} sx={{ p: 2 }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box display="flex" alignItems="center">
                      <IconButton onClick={() => handleToggleCollapse(idx)}>
                        {!collapsedIndexes.has(idx) ? (
                          <ExpandMoreIcon />
                        ) : (
                          <ExpandLessIcon />
                        )}
                      </IconButton>
                      <Typography variant="subtitle1">
                        Device Number (Sensor ID): {sensor.sensor_id}
                      </Typography>
                    </Box>
                    <Box>
                      <Tooltip title="Copy JSON">
                        <IconButton
                          onClick={() => handleCopyToClipboard(sensor)}
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit JSON">
                        <IconButton onClick={() => handleEdit(idx)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Device Number (Sensor ID)">
                        <IconButton
                          onClick={() => handleDeleteSensorClick(idx)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Collapse
                    in={collapsedIndexes.has(idx)}
                    timeout="auto"
                    unmountOnExit
                  >
                    {editIndex === idx ? (
                      <>
                        <Box
                          mt={2}
                          mb={1}
                          display="flex"
                          justifyContent="flex-end"
                          alignItems="center"
                        >
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleAddNewField}
                          >
                            Add New Field
                          </Button>
                          <IconButton
                            color="error"
                            onClick={handleCancelEdit}
                            sx={{ ml: 1 }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                        <TextareaAutosize
                          minRows={10}
                          style={{
                            width: "100%",
                            backgroundColor: "#f4f4f4",
                            padding: "1rem",
                            borderRadius: "6px",
                            fontFamily: "monospace",
                          }}
                          value={editableJson}
                          onChange={(e) => setEditableJson(e.target.value)}
                        />
                        <Box textAlign="right" mt={1}>
                          <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                          >
                            Save
                          </Button>
                        </Box>
                      </>
                    ) : (
                      <Box mt={2}>
                        <pre
                          style={{
                            backgroundColor: "#f4f4f4",
                            padding: "1rem",
                            borderRadius: "6px",
                            overflow: "auto",
                          }}
                        >
                          {JSON.stringify(sensor, null, 2)}
                        </pre>
                      </Box>
                    )}
                  </Collapse>
                </Paper>
              );
            })
          )}
        </Stack>
      )}

      {tabIndex === 1 && (
        <Box mt={3}>
          <FieldsConfiguration selectedDevice={selectedDevice} />
        </Box>
      )}

      <Dialog open={openConfirmDialog} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this Device Number (Sensor ID)? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingPage;
